import { validationConstants } from "constants/formValidation";
import { orderTypeConstants } from "constants/order";
import { distributionLogicOptions } from "constants/paymentPlanConstants";
import { UserRole } from "constants/userRole";
import { useUserWithAccount } from "context/UserAccountProvider";
import { addClearPatientFee, removeClearPatientFee } from "helpers/fee";
import { centsToDollars, dollarsToCents, parsePrice } from "helpers/price";
import useFormValidation from "hooks/useFormValidation";
import { sizer } from "layout/styles/styled/sizer";
import { Label } from "layout/typography/Label";
import {
  getPaymentPlanEnabled,
  getPaymentPlanMinDownPaymentFlatValue,
} from "pages/CreateOrder/utils/helpers";
import { ISetting } from "pages/HospitalsPage/components/PaymentAndRemit/CustomPaymentPlanItem";
import { validateMinDownPaymentFlat } from "pages/OrderView/components/PaymentDetails/helpers/helper";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { useEffect, useMemo, useCallback, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";
import { IAccountResponse } from "types/Account/AccountResponse";
import { IPaymentPlan } from "types/Payment";

import { PaymentPlanDetails } from "./PaymentPlanDetails";

const StyledCard = styled(Card)`
  margin-bottom: ${sizer(4)};
  display: "block";
  .p-card-subtitle {
    color: var(--orange-500);
  }
  .p-card-body {
    padding-left: 0px !important;
  }
  /* Reserve two lines for field labels and bottom-align them so inputs line
     up even when a label (e.g. "Number of payments (downpayment included)")
     wraps to two lines. */
  .formgrid > .field > label {
    min-height: 3em;
    display: flex;
    align-items: flex-end;
  }
`;

const StyledInputNumber = styled(InputNumber)`
  width: 100%;
  input {
    width: 100%;
  }
`;

// TODO: this code should be refactored among with service details component refactor
// at the moment service details component triggers multiple rerenders, because of which useEffects in this component do not work properly
// and some custom settings cases are not applied

export function PaymentPlanSection({
  accountData,
  paymentPlan,
  isRedo,
  isDraft,
  isCopy,
}: {
  accountData: IAccountResponse;
  paymentPlan?: IPaymentPlan;
  isRedo?: boolean;
  isDraft?: boolean;
  isCopy?: boolean;
}) {
  const {
    control,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();
  const { handleOnBlurField, getFormErrorMessage } = useFormValidation();
  const { userInfo } = useUserWithAccount();
  const { paymentPlanDuration, paymentPlanMinDownPaymentPercent } =
    accountData.data;
  const customPaymentPlanSettings = accountData.data.paymentPlanCustomSettings;

  const orderTypeField = watch("orderType");
  const minDownPaymentTypeField = watch("paymentPlanMinDownPaymentType");
  const minDownPaymentPercentField = watch("paymentPlanMinDownPaymentPercent");
  const amountField = watch("amount"); // for GFE
  const totalAmountField = watch("totalAmount"); // for Patient resp and default orders
  const flatAmountField = watch("paymentPlanMinDownPaymentFlat");
  const paymentPlanDurationField = watch("paymentPlanDuration");
  const paymentPlanEnabledField = watch("paymentPlanEnabled");
  const clearPatientFeePercentageField = watch("clearPatientFeePercentage"); // Clear Patient Fee percentage
  const flatAmountWithFeesField = watch("flatAmountWithFees");
  const isSetAmountField = watch("isSetAmount");
  const installmentAmountField = watch("installmentAmount");

  const [showMinPaymentWarning, setShowMinPaymentWarning] = useState(false);

  const paymentPlanIsEnabledForOrderType = useMemo(
    () =>
      getPaymentPlanEnabled(
        orderTypeField,
        accountData.data.paymentPlanTypeEnabled
      ),
    [orderTypeField, accountData.data.paymentPlanTypeEnabled]
  );

  const paymentPlanCustomSetting = useMemo(() => {
    if (!customPaymentPlanSettings?.length) return null;
    let amountToCompare = dollarsToCents(totalAmountField);
    if (orderTypeField === orderTypeConstants.GFE) {
      amountToCompare = dollarsToCents(amountField);
    }
    const sortedSettings = [...customPaymentPlanSettings].sort(
      (a, b) =>
        (a.min ?? Number.NEGATIVE_INFINITY) -
        (b.min ?? Number.NEGATIVE_INFINITY)
    );
    const customSetting = sortedSettings.find((setting: ISetting) => {
      const { min, max } = setting;

      if (
        min !== null &&
        min !== undefined &&
        max !== null &&
        max !== undefined
      ) {
        return amountToCompare >= min && amountToCompare <= max;
      }

      if (
        min !== null &&
        min !== undefined &&
        (max === null || max === undefined)
      ) {
        return amountToCompare >= min;
      }

      return false;
    });
    return customSetting;
  }, [amountField, totalAmountField, orderTypeField]);

  const showPercentageField = useMemo(() => {
    return minDownPaymentTypeField === "Percentage";
  }, [minDownPaymentTypeField]);

  const flatValidationAmount = useMemo(() => {
    if (orderTypeField === orderTypeConstants.GFE) {
      return dollarsToCents(amountField);
    }
    return dollarsToCents(totalAmountField);
  }, [orderTypeField, amountField, totalAmountField]);

  useEffect(() => {
    if (paymentPlanEnabledField && minDownPaymentTypeField === "Flat amount") {
      trigger("paymentPlanMinDownPaymentFlat");
    }
  }, [
    flatValidationAmount,
    paymentPlanEnabledField,
    minDownPaymentTypeField,
    trigger,
  ]);

  const resetPaymentPlanFields = useCallback(() => {
    // payment plan duration does not include first down payment, payment plan duration full includes it
    // to the user we show full duration, but in reality we do all the calculations etc. based on payment plan duration
    setValue("paymentPlanDuration", paymentPlanDuration);
    setValue("paymentPlanDurationFull", paymentPlanDuration + 1);
    setValue(
      "paymentPlanMinDownPaymentPercent",
      paymentPlanMinDownPaymentPercent
    );
    setValue("paymentPlanMinDownPaymentType", "Percentage");
    setValue("paymentPlanMinDownPaymentFlat", null);
    setValue("paymentPlanEnabled", paymentPlanIsEnabledForOrderType);
  }, [
    setValue,
    paymentPlanDuration,
    paymentPlanMinDownPaymentPercent,
    paymentPlanIsEnabledForOrderType,
  ]);

  const setCustomPaymentPlanFields = useCallback(() => {
    const enabled = paymentPlanCustomSetting?.enabled;
    setValue(
      "paymentPlanEnabled",
      paymentPlanIsEnabledForOrderType ? enabled : false
    );
    if (enabled) {
      setValue(
        "paymentPlanDuration",
        paymentPlanCustomSetting.paymentPlanDuration
      );
      setValue(
        "paymentPlanDurationFull",
        paymentPlanCustomSetting.paymentPlanDuration + 1
      );
      setValue(
        "paymentPlanMinDownPaymentType",
        paymentPlanCustomSetting.minDownPaymentType === "percent"
          ? "Percentage"
          : "Flat amount"
      );
      setValue(
        "paymentPlanMinDownPaymentFlat",
        paymentPlanCustomSetting.minDownPaymentFlat === null
          ? null
          : centsToDollars(paymentPlanCustomSetting.minDownPaymentFlat)
      );
      setValue(
        "paymentPlanMinDownPaymentPercent",
        paymentPlanCustomSetting.minDownPaymentPercent
      );
    } else {
      setValue("paymentPlanDuration", paymentPlanDuration);
      setValue("paymentPlanDurationFull", paymentPlanDuration + 1);
      setValue("paymentPlanMinDownPaymentType", "Percentage");
      setValue(
        "paymentPlanMinDownPaymentPercent",
        paymentPlanMinDownPaymentPercent
      );
      setValue("paymentPlanMinDownPaymentFlat", null);
    }
  }, [
    setValue,
    paymentPlanCustomSetting,
    paymentPlanDuration,
    paymentPlanMinDownPaymentPercent,
  ]);

  const shouldResetInstallmentAmountFields = useMemo(() => {
    return (
      (isRedo || isCopy || isDraft) &&
      paymentPlan?.calculationBasis === "InstallmentAmount" &&
      isSetAmountField
    );
  }, [
    isRedo,
    isCopy,
    isDraft,
    paymentPlan?.calculationBasis,
    isSetAmountField,
  ]);

  const resetInstallmentAmountFields = useCallback(() => {
    setValue(
      "installmentAmount",
      centsToDollars(paymentPlan?.installmentAmountInCents || 0)
    );
    setValue("installmentFrequency", paymentPlan?.installmentFrequency);
    setValue("paymentPlanDuration", paymentPlan?.paymentPlanDuration);
    setValue(
      "paymentPlanMinDownPaymentPercent",
      paymentPlan?.paymentPlanMinDownPaymentPercent
        ? paymentPlan?.paymentPlanMinDownPaymentPercent
        : null
    );
    setValue(
      "paymentPlanMinDownPaymentFlat",
      getPaymentPlanMinDownPaymentFlatValue({
        paymentPlanMinDownPaymentFlat:
          paymentPlan?.paymentPlanMinDownPaymentFlat,
        orderType: orderTypeField,
        clearPatientFeeValueInPercentage: clearPatientFeePercentageField,
      }) ?? null
    );
    setValue(
      "paymentPlanMinDownPaymentType",
      paymentPlan?.paymentPlanMinDownPaymentPercent
        ? "Percentage"
        : "Flat amount"
    );
    setValue("paymentPlanEnabled", paymentPlanIsEnabledForOrderType);
  }, [
    setValue,
    paymentPlan,
    orderTypeField,
    clearPatientFeePercentageField,
    paymentPlanIsEnabledForOrderType,
  ]);

  useEffect(() => {
    if (shouldResetInstallmentAmountFields) {
      resetInstallmentAmountFields();
      return;
    }
    if (!paymentPlanCustomSetting || !paymentPlanIsEnabledForOrderType) {
      resetPaymentPlanFields();
    } else {
      setCustomPaymentPlanFields();
    }
  }, [
    isRedo,
    isDraft,
    isCopy,
    isSetAmountField,
    paymentPlanIsEnabledForOrderType,
    paymentPlanCustomSetting,
    resetPaymentPlanFields,
    setCustomPaymentPlanFields,
    resetInstallmentAmountFields,
    shouldResetInstallmentAmountFields,
  ]);

  const subTitle = useMemo(() => {
    if (isSetAmountField) {
      if (
        paymentPlan?.calculationBasis === "InstallmentAmount" &&
        !paymentPlanIsEnabledForOrderType &&
        !paymentPlanEnabledField &&
        isRedo
      ) {
        return "Payment plan enabled field has been disabled. Please review before submitting.";
      }
      return "";
    }
    if (isRedo) {
      return "Payment Plan fields have been recalculated. Please review before submitting.";
    }
    if (isDraft) {
      return "Payment Plan fields have been recalculated. Please review before approving the estimate.";
    }
    return "";
  }, [
    isRedo,
    isDraft,
    isSetAmountField,
    paymentPlanIsEnabledForOrderType,
    paymentPlanEnabledField,
    paymentPlan,
  ]);

  useEffect(() => {
    trigger("paymentPlanDuration");
    trigger("paymentPlanDurationFull");
    trigger("paymentPlanMinDownPaymentPercent");
    trigger("paymentPlanMinDownPaymentFlat");
    trigger("paymentPlanEnabled");
    trigger("paymentPlanMinDownPaymentType");
  }, [
    paymentPlanDurationField,
    minDownPaymentTypeField,
    minDownPaymentPercentField,
    flatAmountField,
    paymentPlanEnabledField,
  ]);

  const estimatedCost = useMemo(() => {
    if (
      !flatValidationAmount ||
      flatValidationAmount <= 0 ||
      orderTypeField !== orderTypeConstants.GFE ||
      !clearPatientFeePercentageField ||
      clearPatientFeePercentageField <= 0
    ) {
      return flatValidationAmount;
    }
    // Convert to dollars, apply fee with rounding, convert back to cents
    const baseInDollars = centsToDollars(flatValidationAmount);
    const totalWithFeesInDollars = addClearPatientFee(
      baseInDollars,
      clearPatientFeePercentageField
    );
    return dollarsToCents(totalWithFeesInDollars);
  }, [flatValidationAmount, clearPatientFeePercentageField]);

  const initialPayment = useMemo(() => {
    if (minDownPaymentTypeField === "Flat amount") {
      // flatAmountField is in dollars, add fee and convert to cents
      const amountWithFee = clearPatientFeePercentageField
        ? addClearPatientFee(flatAmountField, clearPatientFeePercentageField)
        : flatAmountField;
      return dollarsToCents(amountWithFee);
    }
    return estimatedCost * (minDownPaymentPercentField / 100);
  }, [
    estimatedCost,
    minDownPaymentPercentField,
    flatAmountField,
    minDownPaymentTypeField,
    clearPatientFeePercentageField,
  ]);

  // Trigger installmentAmount validation when remaining amount changes
  useEffect(() => {
    if (isSetAmountField) {
      trigger("installmentAmount");
    }
  }, [isSetAmountField, estimatedCost, initialPayment, trigger]);

  const handlePaymentPlanDurationChange = useCallback(
    (e: { value: number | null }, onChange: (value: number | null) => void) => {
      setValue("paymentPlanDuration", e.value != null ? e.value - 1 : null);
      onChange(e.value);
      if (e.value) {
        setValue("paymentPlanMinDownPaymentType", "Flat amount");
        // estimatedCost is in cents, divide by number of payments
        const downPaymentInCents = estimatedCost / e.value;
        const downPaymentInDollars = centsToDollars(downPaymentInCents);
        if (clearPatientFeePercentageField) {
          // Remove fee from the down payment to show base amount without fee
          const downGradedPayment = removeClearPatientFee(
            downPaymentInDollars,
            clearPatientFeePercentageField
          );
          setValue("paymentPlanMinDownPaymentFlat", downGradedPayment);
        } else {
          setValue("paymentPlanMinDownPaymentFlat", downPaymentInDollars);
        }
        setValue("paymentPlanMinDownPaymentPercent", null);
      }
    },
    [setValue, clearPatientFeePercentageField, estimatedCost]
  );

  // Maximum installment amount (remaining amount after downpayment, allowing at least 1 installment)
  const maxInstallmentAmount = useMemo(() => {
    const remainingAmount = estimatedCost - initialPayment;
    if (remainingAmount <= 0) return 0;
    return centsToDollars(remainingAmount);
  }, [estimatedCost, initialPayment]);

  // Maximum allowed payment plan duration
  const MAX_PAYMENT_PLAN_DURATION = 50;

  // Minimum installment amount to keep duration within limit
  const minInstallmentAmount = useMemo(() => {
    const remainingAmount = estimatedCost - initialPayment;
    if (remainingAmount <= 0) return 0;
    // Minimum amount = remaining / max duration (rounded up to ensure we don't exceed max duration)
    return centsToDollars(
      Math.ceil(remainingAmount / MAX_PAYMENT_PLAN_DURATION)
    );
  }, [estimatedCost, initialPayment]);

  const isDownpaymentSameAsMonthlyPayment = useMemo(() => {
    const remainingAmount = estimatedCost - initialPayment;
    const monthlyPayment = centsToDollars(
      Math.floor((remainingAmount / paymentPlanDurationField) * 100) / 100
    );
    return (
      Math.abs((flatAmountWithFeesField || flatAmountField) - monthlyPayment) <=
      0.1
    );
  }, [
    flatAmountField,
    initialPayment,
    flatAmountWithFeesField,
    estimatedCost,
    paymentPlanDurationField,
  ]);

  const handleChangeMinDownPaymentType = (e: any) => {
    setValue("paymentPlanMinDownPaymentType", e.value);
    if (e.value === "Percentage") {
      setValue("paymentPlanMinDownPaymentFlat", null);
      if (
        paymentPlanCustomSetting &&
        paymentPlanCustomSetting.enabled &&
        paymentPlanCustomSetting.minDownPaymentType === "percent"
      ) {
        setValue(
          "paymentPlanMinDownPaymentPercent",
          paymentPlanCustomSetting.minDownPaymentPercent
        );
      } else {
        setValue(
          "paymentPlanMinDownPaymentPercent",
          paymentPlanMinDownPaymentPercent
        );
      }
    } else {
      setValue("paymentPlanMinDownPaymentPercent", null);
      if (
        paymentPlanCustomSetting &&
        paymentPlanCustomSetting.enabled &&
        paymentPlanCustomSetting.minDownPaymentType === "flat"
      ) {
        setValue(
          "paymentPlanMinDownPaymentFlat",
          centsToDollars(paymentPlanCustomSetting.minDownPaymentFlat || 0)
        );
      } else {
        setValue("paymentPlanMinDownPaymentFlat", null);
      }
    }
  };

  // Calculate total flat amount with clear patient fee if it exists
  const flatAmountWithFeesDisplay = useMemo(() => {
    if (
      !flatAmountField ||
      flatAmountField <= 0 ||
      orderTypeField !== orderTypeConstants.GFE ||
      !clearPatientFeePercentageField ||
      clearPatientFeePercentageField <= 0
    ) {
      return null;
    }
    // Add fee with rounding to nearest $5
    const totalWithFees = addClearPatientFee(
      flatAmountField,
      clearPatientFeePercentageField
    );
    return parsePrice(totalWithFees);
  }, [flatAmountField, clearPatientFeePercentageField, orderTypeField]);

  // Set flatAmountWithFees in form data for backend submission
  useEffect(() => {
    if (flatAmountWithFeesDisplay) {
      setValue(
        "flatAmountWithFees",
        parseFloat(flatAmountWithFeesDisplay.replace(/[$,]/g, ""))
      );
    } else {
      setValue("flatAmountWithFees", null);
    }
  }, [flatAmountWithFeesDisplay, setValue]);

  useEffect(() => {
    if (isSetAmountField && !shouldResetInstallmentAmountFields) {
      setValue("paymentPlanMinDownPaymentPercent", null);
      setValue("paymentPlanMinDownPaymentFlat", flatAmountField);
      setValue("paymentPlanMinDownPaymentType", "Flat amount");
    }
  }, [isSetAmountField, shouldResetInstallmentAmountFields]);

  if (!paymentPlan && !paymentPlanIsEnabledForOrderType) {
    return null;
  }
  if (isDraft && paymentPlan && !paymentPlanIsEnabledForOrderType) {
    return null;
  }
  return (
    <StyledCard data-testid="paymentPlan_Card" subTitle={subTitle}>
      <div className="flex flex-column gap-4 w-full">
        <div className="formgrid grid w-full">
          {!isSetAmountField && (
            <div className="field flex flex-column col-12 md:col-8 lg:col-6 xl:col-5 xxl:col-3">
              <Label
                htmlFor="paymentPlanDurationFull"
                data-testid="paymentPlanMonthsFullLabel"
              >
                Number of payments (downpayment included)
              </Label>

              <Controller
                name="paymentPlanDurationFull"
                control={control}
                rules={{
                  required: paymentPlanEnabledField
                    ? validationConstants.PAYMENT_PLAN.DURATION_REQUIRED
                    : false,
                  validate: (value) =>
                    paymentPlanEnabledField
                      ? Number.isInteger(value) ||
                        validationConstants.PAYMENT_PLAN.DURATION_WHOLE_NUMBER
                      : true,
                  min: {
                    value: 2,
                    message: validationConstants.PAYMENT_PLAN.DURATION_MIN,
                  },
                  max: {
                    value: 51,
                    message: validationConstants.PAYMENT_PLAN.DURATION_MAX,
                  },
                }}
                render={({ field: { onChange, value, onBlur } }) => (
                  <>
                    <InputNumber
                      value={value}
                      onBlur={() =>
                        handleOnBlurField({
                          onBlur,
                          field: "paymentPlanDurationFull",
                          trigger,
                        })
                      }
                      inputId="paymentPlanDurationFull"
                      data-testid="months_PaymentPlanFull"
                      onValueChange={(e) =>
                        handlePaymentPlanDurationChange(e, onChange)
                      }
                      onChange={(e) =>
                        e.value !== null &&
                        e.value !== undefined &&
                        setShowMinPaymentWarning(e.value < 2)
                      }
                      min={2}
                      max={51}
                      placeholder="Select number of payments"
                    />
                    {showMinPaymentWarning && (
                      <p className="text-sm my-1 text-orange-500 font-medium">
                        Minimum: 2 payments (today + next installment)
                      </p>
                    )}
                    {getFormErrorMessage("paymentPlanDurationFull", errors)}
                  </>
                )}
              />
            </div>
          )}
          {isSetAmountField && (
            <>
              <div className="field flex flex-column col-12 md:col-4 lg:col-3">
                <Label
                  htmlFor="installmentAmount"
                  data-testid="installmentAmountLabel"
                >
                  Payment Amount
                </Label>
                <Controller
                  name="installmentAmount"
                  control={control}
                  rules={{
                    required: isSetAmountField
                      ? validationConstants.PAYMENT_PLAN
                          .INSTALLMENT_AMOUNT_REQUIRED
                      : false,
                    validate: {
                      maxAmount: (value) => {
                        if (!isSetAmountField || !value) return true;
                        // Add small tolerance for floating point comparison
                        if (
                          value >
                          Math.round(maxInstallmentAmount * 100) / 100
                        ) {
                          return `Installment amount cannot exceed ${parsePrice(
                            maxInstallmentAmount
                          )}`;
                        }
                        return true;
                      },
                      minAmount: (value) => {
                        if (
                          !isSetAmountField ||
                          !value ||
                          minInstallmentAmount <= 0
                        )
                          return true;
                        // Add small tolerance for floating point comparison
                        if (value < minInstallmentAmount - 0.01) {
                          return `Installment amount must be at least ${parsePrice(
                            minInstallmentAmount
                          )} to keep the payment plan within ${MAX_PAYMENT_PLAN_DURATION} payments`;
                        }
                        return true;
                      },
                    },
                  }}
                  render={({ field: { onChange, value, onBlur } }) => (
                    <>
                      <StyledInputNumber
                        value={value}
                        currency="USD"
                        locale="en-US"
                        mode="currency"
                        onBlur={() =>
                          handleOnBlurField({
                            onBlur,
                            field: "installmentAmount",
                            trigger,
                          })
                        }
                        inputId="installmentAmount"
                        data-testid="installmentAmount_PaymentPlan"
                        onValueChange={onChange}
                        min={1}
                        placeholder="Enter amount"
                      />
                      {(maxInstallmentAmount > 0 ||
                        minInstallmentAmount > 0) && (
                        <p className="text-xs mt-1 text-gray-600 font-normal">
                          {minInstallmentAmount > 0 &&
                            `Min: ${parsePrice(minInstallmentAmount)}`}
                          {minInstallmentAmount > 0 &&
                            maxInstallmentAmount > 0 &&
                            " | "}
                          {maxInstallmentAmount > 0 &&
                            `Max: ${parsePrice(maxInstallmentAmount)}`}
                        </p>
                      )}
                      {getFormErrorMessage("installmentAmount", errors)}
                    </>
                  )}
                />
              </div>
              <div className="field flex flex-column col-12 md:col-4 lg:col-3">
                <Label
                  htmlFor="installmentFrequency"
                  data-testid="installmentFrequencyLabel"
                >
                  Frequency
                </Label>
                <Controller
                  name="installmentFrequency"
                  control={control}
                  rules={{
                    required: isSetAmountField
                      ? "Please select a payment frequency"
                      : false,
                  }}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <Dropdown
                        id="installmentFrequency"
                        data-testid="installmentFrequency_PaymentPlan"
                        value={value}
                        onChange={onChange}
                        options={[
                          { label: "Monthly", value: "Monthly" },
                          { label: "Weekly", value: "Weekly" },
                          { label: "BiWeekly", value: "Biweekly" },
                        ]}
                        placeholder="Select frequency"
                        className="w-full"
                      />
                      {getFormErrorMessage("installmentFrequency", errors)}
                    </>
                  )}
                />
              </div>
            </>
          )}
          <div className="field flex flex-column col-12 sm:col-3 md:col-4 lg:col-3 xl:col-1">
            <Label
              htmlFor="paymentPlanMinDownPaymentType"
              data-testid="paymentPlanMinDownPaymentType"
            >
              Type
            </Label>
            <Controller
              name="paymentPlanMinDownPaymentType"
              control={control}
              render={({ field: { onChange, value } }) => (
                <>
                  <Dropdown
                    id="paymentPlanMinDownPaymentType"
                    data-testid="minDownPaymentType_PaymentPlan"
                    value={value}
                    onChange={handleChangeMinDownPaymentType}
                    options={[
                      { label: "%", value: "Percentage" },
                      { label: "$", value: "Flat amount" },
                    ]}
                    placeholder="Select minimum down payment type"
                    className="w-full"
                  />
                  {minDownPaymentTypeField === "Percentage" &&
                  estimatedCost &&
                  minDownPaymentPercentField ? (
                    <p className="text-sm my-1 text-gray-600 font-normal">
                      =
                      {parsePrice(
                        centsToDollars(estimatedCost) *
                          (minDownPaymentPercentField / 100)
                      )}
                    </p>
                  ) : null}
                </>
              )}
            />
          </div>
          <div
            className={`field flex-column col-12 sm:col-9 md:col-4 md:col-4 lg:col-3 pt-0 ${
              showPercentageField ? "flex" : "hidden"
            }`}
          >
            <Label
              htmlFor="paymentPlanMinDownPaymentPercent"
              data-testid="paymentPlanMinDownPaymentPercent"
            >
              First Payment Amount
            </Label>

            <Controller
              name="paymentPlanMinDownPaymentPercent"
              control={control}
              rules={{
                required:
                  showPercentageField && paymentPlanEnabledField
                    ? validationConstants.PAYMENT_PLAN.MIN_DOWN_PAYMENT_PERCENT
                    : false,
              }}
              render={({ field: { onChange, value, onBlur } }) => (
                <>
                  <StyledInputNumber
                    value={value}
                    suffix="%"
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "paymentPlanMinDownPaymentPercent",
                        trigger,
                      })
                    }
                    inputId="paymentPlanMinDownPaymentPercent"
                    data-testid="minDownPaymentPercent_PaymentPlan"
                    onValueChange={onChange}
                    min={5}
                    max={100}
                  />
                  {getFormErrorMessage(
                    "paymentPlanMinDownPaymentPercent",
                    errors
                  )}
                </>
              )}
            />
          </div>
          <div
            className={`field  flex-column col-12 sm:col-9 md:col-4 lg:col-3 ${
              showPercentageField ? "hidden" : "flex"
            }`}
          >
            <Label
              htmlFor="paymentPlanMinDownPaymentFlat"
              data-testid="paymentPlanMinDownPaymentFlat"
            >
              First Payment Amount
            </Label>

            <Controller
              name="paymentPlanMinDownPaymentFlat"
              control={control}
              rules={{
                required:
                  !showPercentageField && paymentPlanEnabledField
                    ? validationConstants.PAYMENT_PLAN.MIN_DOWN_PAYMENT_FLAT
                    : false,
                validate: (value) =>
                  !showPercentageField && paymentPlanEnabledField
                    ? validateMinDownPaymentFlat(
                        value,
                        flatValidationAmount,
                        null
                      )
                    : true,
              }}
              render={({ field: { onChange, value, onBlur } }) => (
                <>
                  <StyledInputNumber
                    value={value}
                    defaultValue={value}
                    currency="USD"
                    locale="en-US"
                    mode="currency"
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "paymentPlanMinDownPaymentFlat",
                        trigger,
                      })
                    }
                    inputId="paymentPlanMinDownPaymentFlat"
                    data-testid="minDownPaymentFlat_PaymentPlan"
                    onValueChange={onChange}
                    min={10}
                    aria-autocomplete="none"
                  />
                  {flatAmountWithFeesDisplay && (
                    <div className="mt-2">
                      <p className="text-sm mb-1 text-primary">
                        with fees: {flatAmountWithFeesDisplay}
                      </p>
                    </div>
                  )}
                  {flatAmountField && (
                    <p className="text-xs mt-1 text-gray-600 font-normal">
                      {isDownpaymentSameAsMonthlyPayment
                        ? "Same as monthly payment (default)"
                        : "Custom amount"}
                    </p>
                  )}
                  {getFormErrorMessage("paymentPlanMinDownPaymentFlat", errors)}
                </>
              )}
            />
          </div>
          {orderTypeField !== orderTypeConstants.PATIENT_RESPONSIBILITY &&
            userInfo?.scope === UserRole.SUPER_ADMIN && (
              <div className="field flex-column col-12 md:col-8 lg:col-6 xl:col-3">
                <Label
                  htmlFor="providerDistributionLogic"
                  data-testid="providerDistributionLogic"
                >
                  Provider Distribution Logic
                </Label>
                <Controller
                  name="providerDistributionLogic"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Dropdown
                      id="providerDistributionLogic"
                      data-testid="providerDistributionLogic_PaymentPlan"
                      value={value}
                      onChange={onChange}
                      options={Object.values(distributionLogicOptions)}
                      placeholder="Select minimum down payment type"
                      className="w-full"
                    />
                  )}
                />
              </div>
            )}
          <div className="field flex flex-column col-12 md:col-4 lg:col-3 xl:col-3 xxl:col-2">
            <Label
              htmlFor="paymentPlanEnabled"
              data-testid="paymentPlanEnabled"
            >
              Payment Plan Enabled
            </Label>
            <Controller
              name="paymentPlanEnabled"
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputSwitch checked={value} onChange={onChange} />
              )}
            />
          </div>
        </div>
        <PaymentPlanDetails
          accountData={accountData}
          estimatedCost={estimatedCost}
          initialPayment={initialPayment}
        />
      </div>
    </StyledCard>
  );
}
